import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FloatingActionButton, GlassPanel, Button, FreemiumMessage, Icon, LoginPromptModal, OnboardingPrompt } from '../components/common'
import { useAuth } from '../context/AuthContext'
import { useOnboardingContext } from '../context/OnboardingContext'
import { getUserProjects, createProject, PROJECT_ROLES } from '../services/projects'
import { BRANDING } from '../config/branding'
import './ProjectsListView.css'

// Page transition variants
const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(4px)'
  },
  animate: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(4px)',
    transition: {
      duration: 0.2
    }
  }
}

// Container variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
}

// Card animation variants
const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 20,
    scale: 0.95,
  },
  visible: { 
    opacity: 1, 
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.4, 0, 0.2, 1],
    },
  },
}

/**
 * Format a Firestore timestamp for display
 */
const formatDate = (timestamp) => {
  if (!timestamp) return ''
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Get role badge configuration
 */
const getRoleBadge = (role) => {
  switch (role) {
    case PROJECT_ROLES.ADMIN:
      return { label: 'Admin', className: 'projects-view__role--admin' }
    case PROJECT_ROLES.GUEST:
      return { label: 'Guest', className: 'projects-view__role--guest' }
    case PROJECT_ROLES.CUSTOM:
      return { label: 'Custom', className: 'projects-view__role--custom' }
    default:
      return { label: 'Member', className: 'projects-view__role--guest' }
  }
}

/**
 * ProjectCard - Individual project display card
 */
function ProjectCard({ project, onClick, index }) {
  const roleBadge = getRoleBadge(project.userRole)
  
  return (
    <motion.div
      variants={cardVariants}
      className="projects-view__card"
      onClick={onClick}
      whileHover={{ 
        y: -4, 
        transition: { duration: 0.2 } 
      }}
      whileTap={{ scale: 0.98 }}
    >
      <GlassPanel 
        hoverable 
        padding="lg" 
        className="projects-view__card-inner"
        glow={project.userRole === PROJECT_ROLES.ADMIN ? 'amber' : 'cyan'}
      >
        {/* Role Badge */}
        <span className={`projects-view__role ${roleBadge.className}`}>
          {roleBadge.label}
        </span>
        
        {/* Project Icon */}
        <div className="projects-view__card-icon"><Icon name="projects" size={32} /></div>
        
        {/* Project Name */}
        <h3 className="projects-view__card-name">{project.name}</h3>
        
        {/* Description */}
        {project.description && (
          <p className="projects-view__card-description">
            {project.description.length > 100 
              ? project.description.slice(0, 100) + '...' 
              : project.description}
          </p>
        )}
        
        {/* Meta Info */}
        <div className="projects-view__card-meta">
          <span className="projects-view__card-date">
            Created {formatDate(project.createdAt)}
          </span>
        </div>
      </GlassPanel>
    </motion.div>
  )
}

/**
 * CreateProjectModal - Modal for creating new projects
 */
function CreateProjectModal({ isOpen, onClose, onCreated }) {
  const { user } = useAuth()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [isOpen])

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('')
      setDescription('')
      setError(null)
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!name.trim()) {
      setError('Project name is required')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const projectId = await createProject(user.uid, {
        name: name.trim(),
        description: description.trim(),
      })
      
      onCreated(projectId)
      onClose()
    } catch (err) {
      console.error('Failed to create project:', err)
      setError(err.message || 'Failed to create project')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="projects-view__modal-overlay" onClick={onClose}>
      <motion.div 
        className="projects-view__modal"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        <GlassPanel glow="cyan" padding="xl">
          <h2 className="projects-view__modal-title">Create New Project</h2>
          
          <form onSubmit={handleSubmit} className="projects-view__modal-form">
            <div className="projects-view__form-group">
              <label htmlFor="project-name" className="projects-view__label">
                Project Name *
              </label>
              <input
                ref={inputRef}
                id="project-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={BRANDING.defaults.projectName}
                className="projects-view__input"
                disabled={loading}
                maxLength={100}
              />
            </div>

            <div className="projects-view__form-group">
              <label htmlFor="project-description" className="projects-view__label">
                Description
              </label>
              <textarea
                id="project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What's this project about?"
                className="projects-view__textarea"
                disabled={loading}
                rows={3}
                maxLength={500}
              />
            </div>

            {error && (
              <p className="projects-view__error">{error}</p>
            )}

            <div className="projects-view__modal-actions">
              <Button 
                variant="ghost" 
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                loading={loading}
              >
                Create Project
              </Button>
            </div>
          </form>
        </GlassPanel>
      </motion.div>
    </div>
  )
}

/**
 * ProjectsListView - Main project selection screen
 * 
 * Accessible without login. Anonymous users see empty state.
 * Creating a project requires login - shows LoginPromptModal.
 */
export default function ProjectsListView() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const fabRef = useRef(null)
  const { needsOnboarding, startOnboarding, updateEntityCounts } = useOnboardingContext()
  
  // State
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [loginPromptOpen, setLoginPromptOpen] = useState(false)
  const [showOnboardingPrompt, setShowOnboardingPrompt] = useState(true)

  // Fetch projects on mount (only if logged in)
  useEffect(() => {
    let mounted = true

    const fetchProjects = async () => {
      // Don't try to fetch if not logged in
      if (!user) {
        setProjects([])
        setLoading(false)
        return
      }
      
      setLoading(true)
      setError(null)

      try {
        const userProjects = await getUserProjects(user.uid)
        if (mounted) {
          setProjects(userProjects)
          // Update onboarding context with project count
          updateEntityCounts({ projects: userProjects.length })
        }
      } catch (err) {
        console.error('Failed to load projects:', err)
        if (mounted) {
          setError(err.message || 'Failed to load projects')
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    fetchProjects()

    return () => {
      mounted = false
    }
  }, [user, updateEntityCounts])

  // Handle "Create Project" click - check auth first
  const handleCreateClick = () => {
    if (!user) {
      // Not logged in - show login prompt
      setLoginPromptOpen(true)
    } else {
      // Logged in - show create modal
      setModalOpen(true)
    }
  }

  // Handle project creation
  const handleProjectCreated = async (projectId) => {
    // Refresh projects list
    try {
      const userProjects = await getUserProjects(user.uid)
      setProjects(userProjects)
    } catch (err) {
      console.error('Failed to refresh projects:', err)
    }
  }

  // Handle project click - navigate to project
  const handleProjectClick = (project) => {
    navigate(`/projects/${project.id}`)
  }

  // For anonymous users, show welcome message
  const isAnonymous = !user

  return (
    <motion.div 
      className="projects-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Page Header */}
      <header className="projects-view__header">
        <div className="projects-view__title-group">
          <span className="projects-view__icon">{BRANDING.logo.icon}</span>
          <h1 className="projects-view__title">{BRANDING.name}</h1>
        </div>
        <p className="projects-view__subtitle">
          {isAnonymous 
            ? 'Your story universe awaits'
            : loading 
              ? 'Loading projects...' 
              : `${projects.length} project${projects.length !== 1 ? 's' : ''}`
          }
        </p>
        <p className="projects-view__tagline">{BRANDING.tagline}</p>
      </header>

      {/* Error State */}
      {error && (
        <GlassPanel glow="amber" padding="lg" className="projects-view__error-panel">
          <h3>Error loading projects</h3>
          <p>{error}</p>
        </GlassPanel>
      )}

      {/* Loading State (only for logged in users) */}
      {loading && !error && user && (
        <div className="projects-view__loading">
          <div className="projects-view__spinner" />
          <p>Loading your projects...</p>
        </div>
      )}

      {/* Onboarding Prompt for new users */}
      {!loading && user && needsOnboarding && showOnboardingPrompt && (
        <OnboardingPrompt onDismiss={() => setShowOnboardingPrompt(false)} />
      )}

      {/* Empty State (includes anonymous users) */}
      {!loading && !error && projects.length === 0 && (
        <div className="projects-view__empty-container">
          <GlassPanel glow="cyan" padding="xl" className="projects-view__empty">
            <div className="projects-view__empty-icon">
              <Icon name="projects" size={64} />
            </div>
            <h2>{isAnonymous ? 'Welcome to Plot Planet' : 'No projects yet'}</h2>
            <p>
              {isAnonymous 
                ? 'Create your first project to start building your story universe. Characters, timelines, locations, and more.'
                : 'Create your first project to start building your story bible.'
              }
            </p>
            <button 
              className="projects-view__empty-btn"
              onClick={handleCreateClick}
              data-onboarding="create-project"
            >
              Create your first project
            </button>
          </GlassPanel>
          
          <FreemiumMessage 
            variant="default" 
            showCreditsInfo={true}
            className="projects-view__freemium-message" 
          />
        </div>
      )}

      {/* Projects Grid */}
      {!loading && !error && projects.length > 0 && (
        <motion.div 
          className="projects-view__grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {projects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
              onClick={() => handleProjectClick(project)}
            />
          ))}
        </motion.div>
      )}

      {/* Floating Action Button */}
      <div ref={fabRef} data-onboarding="create-project">
        <FloatingActionButton
          icon={<Icon name="plus" size={20} />}
          variant="primary"
          size="lg"
          position="bottom-right"
          label="Create new project"
          onClick={handleCreateClick}
        />
      </div>

      {/* Create Project Modal (only shown when logged in) */}
      <CreateProjectModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={handleProjectCreated}
      />

      {/* Login Prompt Modal (shown when anonymous user tries to create) */}
      <LoginPromptModal
        isOpen={loginPromptOpen}
        onClose={() => setLoginPromptOpen(false)}
        action="create"
        returnTo="/"
      />
    </motion.div>
  )
}
